import { type DragEvent, useCallback } from 'react';

import { type Clip } from '@air/api/types';
import {
  CANVAS_ASSET_DROP_TYPE,
  CANVAS_ASSET_GRAB_OFFSET_TYPE,
  CANVAS_BOARD_DROP_TYPE,
  clipToCanvasAssetPayload,
} from '@air/feature-edit-canvas';

import { setDragGrabAnchor } from '~/components/Shared/Drag/dragGrabAnchor';
import { type AssetDragItem, type BoardDragItem } from '~/components/Shared/Drag/dragTypes';
import { isAssetDragItem } from '~/components/Shared/Drag/utils/isAssetDragItem';
import { isBoardDragItem } from '~/components/Shared/Drag/utils/isBoardDragItem';

export type UseHTML5DragStartParams = {
  item: BoardDragItem | AssetDragItem;
  isSelected: boolean;
  selectedAssets: Clip[];
  selectedBoardCount: number;
};

/**
 * Creates an onDragStart handler that sets HTML5 dataTransfer data for canvas/chat drop targets.
 * For boards, sets the board count so the canvas can show a rejection toast.
 * For assets, serializes asset payloads so the canvas can create image/video nodes on drop.
 */
export function useHTML5DragStart({ item, isSelected, selectedAssets, selectedBoardCount }: UseHTML5DragStartParams) {
  const handleDragStart = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!e.dataTransfer) {
        return;
      }

      if (isBoardDragItem(item)) {
        const boardCount = isSelected && selectedBoardCount > 0 ? selectedBoardCount : 1;
        e.dataTransfer.setData(CANVAS_BOARD_DROP_TYPE, String(boardCount));
        e.dataTransfer.effectAllowed = 'all';
        setDragGrabAnchor(0, 0);
        return;
      }

      if (!isAssetDragItem(item)) {
        return;
      }

      const assetsToSerialize = isSelected && selectedAssets.length > 0 ? selectedAssets : [item.asset];
      const payloads = assetsToSerialize.map(clipToCanvasAssetPayload);
      e.dataTransfer.setData(CANVAS_ASSET_DROP_TYPE, JSON.stringify(payloads));
      e.dataTransfer.setData('text/plain', item.asset.id);
      e.dataTransfer.effectAllowed = 'all';

      const thumbnailEl = e.currentTarget.querySelector<HTMLElement>('img, video') ?? e.currentTarget;
      const rect = thumbnailEl.getBoundingClientRect();
      const anchorX = rect.width > 0 ? Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0;
      // Compute grab offset for the canvas drop target (CANVAS_ASSET_GRAB_OFFSET_TYPE).
      // The gallery card img is object-cover center-cropped to fit the thumbnail display area (which
      // is shorter than the clip's natural height when a title footer is visible), so we offset by
      // the center-crop amount before normalizing. Note: setDragGrabAnchor(0,0) is intentional —
      // the react-dnd drag layer uses a fixed cursor offset; anchorX/Y are for the canvas only.
      const { width: clipW, height: clipH } = item.asset;
      const aspectRatio = clipW && clipH ? clipW / clipH : null;
      const naturalHeight = aspectRatio !== null && rect.width > 0 ? rect.width / aspectRatio : rect.height;
      const cropTop = Math.max(0, (naturalHeight - rect.height) / 2);
      const anchorY =
        naturalHeight > 0 ? Math.min(1, Math.max(0, (cropTop + (e.clientY - rect.top)) / naturalHeight)) : 0;

      setDragGrabAnchor(0, 0);
      e.dataTransfer.setData(CANVAS_ASSET_GRAB_OFFSET_TYPE, JSON.stringify({ x: anchorX, y: anchorY }));
    },
    [item, isSelected, selectedAssets, selectedBoardCount],
  );

  return { handleDragStart };
}
